import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  Course,
  CourseAttempt,
  CourseStatusAttemptEnum,
  InteractiveElement,
  InteractiveElementType,
} from '@/client/courses';
import {
  AnswerOption,
  ImageSettings,
  ModifiedAnswerOption,
  ModifiedQuestion,
  ModifiedQuiz,
} from '@/client/quizzes';
import { LoadingStatuses } from '@/common/constants';
import { useToast } from '@/hooks/useToast';
import ImageMock from '@/images/placeholders/working-from-home.svg';
import hardcodedTranslations from '@/locales/hardcodedTranslations';
import { AppSpinner } from '@/ui/app-spinner';
import { AppButton } from '@/ui/buttons';
import { ProgressHeader } from '@/ui/progress-header/ProgressHeader';
import { FlexContainer } from '@/ui/styled-ui';
import { modifyCourseQuiz } from '@/utils/helpers/courses.helper';
import { isMobile } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { Image } from 'primereact/image';
import { RadioButtonChangeEvent } from 'primereact/radiobutton';
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { ContentEmailComponent } from '../courses/elements/ContentEmailComponent';
import { ContentSMSComponent } from '../courses/elements/ContentSMSComponent';
import { QuizAnswerFeedback } from './QuizAnswerFeedback';
import { QuizAnswerOption } from './QuizAnswerOption';
import { QuizEnd } from './QuizEnd';

const StyledQuizContainer = styled(FlexContainer)<{ isMobile: boolean }>`
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  max-width: 720px;

  ${({ isMobile }) =>
    isMobile &&
    css`
      padding: var(--small-padding);
    `}
`;

const StyledQuizQuestionContainer = styled(FlexContainer)<{
  isMobile: boolean;
}>`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--default-padding);
  padding-bottom: var(--big-padding);
  border-bottom: 1px solid var(--gray-dark);

  ${({ isMobile }) =>
    isMobile &&
    css`
      gap: var(--small-padding);
      margin-bottom: 0px;
      border-bottom: none;

      @media (orientation: portrait) {
        flex-direction: column-reverse;
      }
    `}
`;

const StyledQuizQuestionText = styled(FlexContainer)`
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  height: 100%;
`;

const StyledQuizQuestionImage = styled(FlexContainer)<{
  isMobile: boolean;
  withBorder: boolean;
}>`
  flex: 1;
  align-items: center;
  justify-content: center;
  width: auto;
  max-width: 280px;
  overflow: hidden;
  border-radius: var(--small-border-radius);
  border: ${({ withBorder }) =>
    withBorder ? '4px solid var(--gray-dark)' : 'none'};

  img {
    width: 100%;
    height: 100%;
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      flex: auto;
      min-height: 120px;
      width: 100%;
      margin: var(--default-padding) auto;
      margin-top: 0;
    `}
`;

const StyledQuestionText = styled.div`
  font-size: var(--heading-small-font-size);
  line-height: 140%;
  margin-bottom: var(--default-padding);
  white-space: pre-line;
`;

const StyledMultiChoiceText = styled.div`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  margin-bottom: var(--medium-padding);
`;

const StyledQuizNavigationContainer = styled(FlexContainer)<{ rtl?: boolean }>`
  flex-direction: ${({ rtl }) => (rtl ? 'row-reverse' : 'row')};
  justify-content: flex-end;
  gap: var(--small-padding);
  padding-top: var(--semi-big-padding);

  .p-button.p-button-outlined {
    background: transparent !important;
  }
`;

const StyledLoadingContainer = styled(FlexContainer)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--default-padding);
`;

type QuizProps = {
  quizId?: string;
  course: Course;
  attemptLanguage: LanguagesEnum;
  rtl?: boolean;
  courseAttempt?: CourseAttempt;
  onComplete?: (score: number) => void;
  onFinish: () => void;
  handleBackToCourse?: () => void;
  isPreview?: boolean;
  status?: CourseStatusAttemptEnum;
  hasPreviousModules?: boolean;
};

const getTranslatedContent = (
  item: ModifiedQuestion | ModifiedAnswerOption | undefined,
  language: LanguagesEnum,
  field = 'text',
): string => {
  if (!item) return '';

  const translation = item.translations[language];
  if (!translation) return '';

  switch (field) {
    case 'text':
      return translation.text || '';
    case 'file':
      return translation.file?.signedUrl || '';
    case 'explanation':
      return translation.explanation || '';
    default:
      return '';
  }
};

type QuestionTranslations = {
  text: string;
  image: string;
  imageSettings?: ImageSettings;
  explanation: string;
  interactiveElement?: InteractiveElement;
};

type AnswerOptionType = {
  id: string;
  text: string;
  isCorrect: boolean;
};

const useQuestionTranslations = (
  question: ModifiedQuestion | ModifiedAnswerOption | undefined,
  language: LanguagesEnum,
): QuestionTranslations => {
  return useMemo(
    () => ({
      text: getTranslatedContent(question, language, 'text'),
      image: getTranslatedContent(question, language, 'file'),
      explanation: getTranslatedContent(question, language, 'explanation'),
      imageSettings: question?.translations?.[language]?.imageSettings,
      interactiveElement:
        question?.translations?.[language]?.interactiveElement,
    }),
    [question, language],
  );
};

export const Quiz: React.FC<QuizProps> = ({
  quizId,
  course,
  isPreview = false,
  onComplete,
  onFinish,
  handleBackToCourse,
  attemptLanguage,
  rtl,
  courseAttempt,
  hasPreviousModules = false,
}) => {
  const toast = useToast();
  const [selectedOptions, setSelectedOptions] = useState<AnswerOptionType[]>(
    [],
  );
  const [isCorrect, setIsCorrect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);

  const [quiz, setQuiz] = useState<ModifiedQuiz | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [isMultiChoice, setIsMultiChoice] = useState(false);
  const [explanation, setExplanation] = useState<string>('');

  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isQuizComplete, setIsQuizComplete] = useState(false);

  useEffect(() => {
    const fetchQuiz = async () => {
      if (!quizId) return;

      try {
        const response = await client.courses.getCourseQuiz(course.id, quizId);

        setQuiz(modifyCourseQuiz(response));
      } catch (error) {
        handleAxiosError(error as AxiosError, toast);
      }
    };

    const fetchLastAttempt = async () => {
      if (!quizId) return;
      try {
        const lastAttempt = await client.quizzes.getLastQuizAttempt(quizId);

        if (lastAttempt.status !== CourseStatusAttemptEnum.COMPLETED) {
          setCurrentQuestionIndex(lastAttempt.userQuestionAnswers.length);
        }
      } catch (error: any) {
        if (error?.statusCode === 404) {
          return;
        }

        handleAxiosError(error as AxiosError, toast);
      }
    };

    setIsLoading(true);
    fetchQuiz();
    fetchLastAttempt();
    setIsLoading(false);
  }, [quizId, course.id, attemptLanguage]);

  useEffect(() => {
    const correctAnswers =
      quiz?.questions[currentQuestionIndex]?.answerOptions?.filter(
        (option: AnswerOption) => option.isCorrect,
      ).length || 0;
    setIsMultiChoice(correctAnswers > 1);
  }, [quiz, currentQuestionIndex]);

  const currentQuestion = quiz?.questions?.[currentQuestionIndex];
  const {
    text,
    image,
    explanation: questionExplanation,
    imageSettings,
    interactiveElement,
  } = useQuestionTranslations(currentQuestion, attemptLanguage);

  const selectedAnswerText = useMemo(() => {
    const correctAnswers = quiz?.questions[currentQuestionIndex]?.answerOptions
      ?.filter((option: ModifiedAnswerOption) => option.isCorrect)
      .map((option: ModifiedAnswerOption) =>
        getTranslatedContent(option, attemptLanguage),
      );
    return correctAnswers?.join(', ');
  }, [currentQuestion, selectedOptions, attemptLanguage]);

  const createAnswerOption = (
    answerOption: ModifiedAnswerOption,
    language: LanguagesEnum,
  ): AnswerOptionType => ({
    id: answerOption.id || '',
    text: getTranslatedContent(answerOption, language),
    isCorrect: !!answerOption.isCorrect,
  });

  const handleOptionSelect = (
    answerOption: ModifiedAnswerOption,
    e: CheckboxChangeEvent | RadioButtonChangeEvent,
  ) => {
    const isSelected = e.checked;
    const newOption = createAnswerOption(answerOption, attemptLanguage);

    setSelectedOptions((currentOptions) => {
      if (!isMultiChoice) {
        return isSelected ? [newOption] : [];
      }

      return isSelected
        ? currentOptions.concat(newOption)
        : currentOptions.filter((option) => option.id !== newOption.id);
    });
  };

  const handleNext = async () => {
    // Early return if required data is missing
    if (!currentQuestion?.id || !quizId) return;

    setIsSubmittingAnswer(true);

    try {
      // Get selected answers
      const selectedAnswerIds = selectedOptions.map(({ id }) => id);

      if (!isPreview && courseAttempt) {
        // Submit answer and get response
        const response = await client.learnerCourses.answerQuizQuestion(
          course.id,
          quizId,
          currentQuestion.id,
          selectedAnswerIds,
          courseAttempt.id,
        );

        // Update course attempt with new quiz attempt
        await client.learnerCourses.updateCourseAttempt({
          courseId: course.id,
          attemptId: courseAttempt.id,
          language: attemptLanguage,
          quizAttempts: [
            ...(courseAttempt.quizAttempts || []),
            response.quizAttemptId,
          ],
        });

        // Update quiz state
        setScore(response.score || 0);
        setIsCorrect(!!response?.isCorrect);
      } else {
        const correctAnswerCount =
          currentQuestion?.answerOptions?.filter(
            (option: AnswerOption) => option.isCorrect,
          ).length || 0;

        const isCorrect =
          selectedOptions.length === correctAnswerCount &&
          selectedOptions.every((option) => option.isCorrect);
        setIsCorrect(isCorrect);

        if (isCorrect) {
          setCorrectAnswers((prev) => prev + 1);
          const totalQuestions = quiz?.questions?.length || 0;
          const newScore = Math.round(
            (100 * (correctAnswers + 1)) / totalQuestions,
          );
          setScore(newScore);
        }
      }

      setIsSubmittingAnswer(false);

      setExplanation(questionExplanation);

      // Handle quiz progression
      const isLastQuestion =
        currentQuestionIndex + 1 >= (quiz?.questions?.length || 0);

      if (isLastQuestion && !questionExplanation) {
        setIsQuizComplete(true);
      } else if (!questionExplanation) {
        setCurrentQuestionIndex((prev) => prev + 1);
        setSelectedOptions([]);
      }
    } catch (error) {
      handleAxiosError(error as AxiosError, toast);
    }
  };

  const handleRetry = async () => {
    setIsQuizComplete(false);
    setCurrentQuestionIndex(0);
    setSelectedOptions([]);
    setScore(0);
    setCorrectAnswers(0);
    setExplanation('');
  };

  const hasSelectedOption = useMemo(() => {
    return selectedOptions.length > 0;
  }, [selectedOptions]);

  const hasAnsweredMultipleChoice = useMemo(() => {
    if (!isMultiChoice) return true;

    return selectedOptions.length > 1;
  }, [isMultiChoice, selectedOptions]);

  if (isLoading) {
    return (
      <StyledLoadingContainer>
        <AppSpinner />
      </StyledLoadingContainer>
    );
  }

  if (isQuizComplete) {
    return (
      <QuizEnd
        course={course}
        score={score}
        attemptLanguage={attemptLanguage}
        onRetry={handleRetry}
        onFinish={onFinish}
        onComplete={() => onComplete?.(score)}
        rtl={rtl}
      />
    );
  }

  return (
    <>
      <StyledQuizContainer isMobile={isMobile()} dir={rtl ? 'rtl' : 'ltr'}>
        <ProgressHeader
          className="quiz-progress-header"
          currentIndex={currentQuestionIndex}
          attemptLanguage={attemptLanguage}
          type="quiz"
          totalCount={quiz?.questions?.length || 0}
        />

        <StyledQuizQuestionContainer isMobile={isMobile()}>
          <StyledQuizQuestionText>
            <StyledQuestionText>{text}</StyledQuestionText>
            {isMultiChoice && (
              <StyledMultiChoiceText>
                {
                  hardcodedTranslations.quiz.answer.selectAllOptionsThatApply[
                    attemptLanguage
                  ]
                }
              </StyledMultiChoiceText>
            )}
            {currentQuestion?.answerOptions?.map(
              (answerOption: ModifiedAnswerOption) => (
                <QuizAnswerOption
                  key={answerOption.id}
                  answerOption={{
                    id: answerOption.id || '',
                    text: getTranslatedContent(answerOption, attemptLanguage),
                    isCorrect: !!answerOption.isCorrect,
                    type: answerOption.type,
                  }}
                  isMultiChoice={isMultiChoice}
                  isSelected={selectedOptions.some(
                    (option) => option.id === answerOption.id,
                  )}
                  onOptionSelect={(e) => handleOptionSelect(answerOption, e)}
                />
              ),
            )}
          </StyledQuizQuestionText>
          {interactiveElement ? (
            interactiveElement.type === InteractiveElementType.EMAIL ? (
              <ContentEmailComponent
                id={currentQuestion?.id}
                key={currentQuestion?.id}
                interactiveElement={interactiveElement}
                readOnly={true}
              />
            ) : interactiveElement.type === InteractiveElementType.SMS ? (
              <ContentSMSComponent
                id={currentQuestion?.id}
                key={currentQuestion?.id}
                interactiveElement={interactiveElement}
                readOnly={true}
                rtl={rtl}
              />
            ) : null
          ) : image ? (
            <StyledQuizQuestionImage
              isMobile={isMobile()}
              withBorder={imageSettings?.withBorder || false}
            >
              <Image
                src={image || ImageMock}
                alt="Quiz question"
                preview={imageSettings?.withZoom || false}
              />
            </StyledQuizQuestionImage>
          ) : null}
        </StyledQuizQuestionContainer>

        <StyledQuizNavigationContainer rtl={rtl} dir="ltr">
          {hasPreviousModules && (
            <AppButton
              label={
                hardcodedTranslations.buttons.backToCourse[attemptLanguage]
              }
              type="outlined"
              onClick={handleBackToCourse}
            />
          )}
          <AppButton
            label={hardcodedTranslations.buttons.next[attemptLanguage]}
            severity="success"
            icon={`pi ${rtl ? 'pi-arrow-left' : 'pi-arrow-right'}`}
            iconPos="right"
            rtl={rtl}
            onClick={handleNext}
            state={
              isSubmittingAnswer
                ? LoadingStatuses.LOADING
                : LoadingStatuses.IDLE
            }
            tooltip={
              !hasAnsweredMultipleChoice
                ? hardcodedTranslations.quiz.youNeedToSelectMultipleAnswers[
                    attemptLanguage
                  ]
                : undefined
            }
            tooltipOptions={{
              showOnDisabled: true,
              position: 'top',
            }}
            isDisabled={!hasSelectedOption || !hasAnsweredMultipleChoice}
          />
        </StyledQuizNavigationContainer>
      </StyledQuizContainer>
      {explanation && (
        <QuizAnswerFeedback
          isCorrect={isCorrect}
          explanation={explanation}
          selectedAnswer={selectedAnswerText}
          onHide={() => {
            setExplanation('');
            const isLastQuestion =
              currentQuestionIndex + 1 >= (quiz?.questions?.length || 0);

            if (isLastQuestion) {
              setIsQuizComplete(true);
            } else {
              setCurrentQuestionIndex((prev) => prev + 1);
            }

            setSelectedOptions([]);
          }}
          attemptLanguage={attemptLanguage}
          rtl={rtl}
        />
      )}
    </>
  );
};
